/**
 * **Die Checkliste eines zusammengelegten Termins** — vor dem Aktivieren, an einer Beispielanlage.
 *
 * Mehrere Pflichten derselben Anlage mit gleichem Intervall und gleicher Ausführendenklasse ergeben eine
 * Checkliste (tri-server `checklistMerge`). Hier steht, welche Pflichten sie bedient, wer sie durchführen
 * muss, welche Frist jede Pflicht hat und woher (Norm, Herstellerunterlage, Betreiber), und alle Prüfpunkte
 * mit ihrer Herkunft — dieselbe Ableitung wie beim Aktivieren (`previewMergedChecklist`).
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAtom } from "jotai";
import { authenticationAtom } from "../../State";
import { useLegalBindingAPI } from "../../LegalBindingAPI";
import { useModalContext } from "../../components/ModalProvider";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const asText = (value: unknown): string => {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

export interface MergedAppointmentRef {
  obligationKeys: string[];
  sampleAssetId: string;
  sampleAssetName?: string | null;
  intervalMonths: number;
}

interface Properties {
  appointment: MergedAppointmentRef | null;
  bindingId?: string;
  onClose: () => void;
}

export default function MergedChecklistPreviewDialog({ appointment, bindingId, onClose }: Properties) {
  const { t } = useTranslation();
  const { pushError } = useModalContext() as any;
  const [authentication] = useAtom(authenticationAtom) as any;
  const legalAPI = useLegalBindingAPI(authentication?.customer);
  const [preview, setPreview] = useState<any>(null);

  useEffect(() => {
    if (!appointment) return;
    let cancelled = false;
    setPreview(null);
    legalAPI
      .getMergedChecklistPreview(appointment.obligationKeys, appointment.sampleAssetId, bindingId)
      .then((result: any) => !cancelled && setPreview(result))
      .catch((error: any) => {
        if (!cancelled) {
          pushError(error);
          onClose();
        }
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appointment]);

  const intervalOf = (key: string) => (preview?.intervals ?? []).find((i: any) => i.obligationKey === key);
  const sourceLabel = (interval: any): string => {
    if (!interval || interval.months == null) return t("operatorObligationsPanel.merged.intervalOpen");
    const base = t("operatorObligationsPanel.merged.everyMonths", { count: interval.months });
    if (interval.source === "manufacturer") {
      const page = interval.manufacturer?.page ? `, S. ${interval.manufacturer.page}` : "";
      return `${base} — ${t("operatorObligationsPanel.merged.sourceManufacturer")}: ${interval.manufacturer?.documentName ?? ""}${page}`;
    }
    return `${base} — ${t(`operatorObligationsPanel.merged.source.${interval.source}`, interval.source ?? "")}`;
  };

  return (
    <Dialog open={!!appointment} onOpenChange={(open: boolean) => !open && onClose()}>
      <DialogContent className="max-h-[90vh] w-[min(1100px,95vw)] max-w-none overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="break-words pr-8">
            {t("operatorObligationsPanel.merged.previewTitle", { count: appointment?.obligationKeys.length ?? 0 })}
          </DialogTitle>
          <DialogDescription className="break-words">
            {t("operatorObligationsPanel.merged.previewSample", {
              asset: preview?.asset?.name ?? appointment?.sampleAssetName ?? "",
              months: appointment?.intervalMonths ?? "",
            })}
          </DialogDescription>
        </DialogHeader>
        {!preview ? (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-6 w-1/2" />
            <Skeleton className="h-32 w-full" />
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <div className="rounded-md border bg-muted/40 p-3 text-sm" data-testid="merged-served-obligations">
              <div className="mb-1 font-semibold">{t("checklist.servesObligations")}</div>
              <ul className="list-disc space-y-1 pl-5">
                {(preview.servedObligations ?? []).map((duty: any) => (
                  <li key={`${duty.obligationKey}@${duty.version}`} className="break-words">
                    <span className="font-medium">{[duty.regulation, duty.provisionId].filter(Boolean).join(" ")}</span>
                    {duty.description ? ` — ${duty.description}` : ""}
                    <div className="text-xs text-muted-foreground">{sourceLabel(intervalOf(duty.obligationKey))}</div>
                  </li>
                ))}
              </ul>
              {preview.requiredPerformer ? (
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <span className="text-muted-foreground">{t("checklist.requiredPerformer")}:</span>
                  <Badge variant="secondary">
                    {t(`checklist.performer.${preview.requiredPerformer}`, { defaultValue: preview.requiredPerformer })}
                  </Badge>
                </div>
              ) : null}
            </div>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("operatorObligationsPanel.merged.item")}</TableHead>
                    <TableHead>{t("operatorObligationsPanel.merged.interval")}</TableHead>
                    <TableHead>{t("operatorObligationsPanel.merged.origin")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(preview.items ?? []).map((item: any, index: number) => (
                    <TableRow key={index}>
                      <TableCell className="break-words">
                        {asText(item.task)}
                        {item.sourceText && item.sourceText !== item.task ? (
                          <div className="mt-0.5 text-xs italic text-muted-foreground">„{item.sourceText}“</div>
                        ) : null}
                      </TableCell>
                      <TableCell className="break-words">{asText(item.intervall)}</TableCell>
                      <TableCell className="break-words text-xs text-muted-foreground">{asText(item.normbezug)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
